import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import API from "../../../api";
import { archiveEnrollment, activateEnrollment } from "../../../api/enrollments";
import ArchiveEnrollmentModal from "../../../components/enrollments/ArchiveEnrollmentModal";
import {
    formatChargesMode,
    isPeriodBillingMode,
    formatPeriodInterval,
    periodChargeStatusLabel,
    resolvePeriodCharge,
    resolvePeriodInterval,
    enrollmentChargeSnapshot,
} from "../../../utils/periodBillingCopy";
import UserLink from "../../../components/common/UserLink";
import PageHeaderSection from "../../../components/layout/PageHeaderSection";
import PageLoadingShell from "../../../components/layout/PageLoadingShell";
import Button from "../../../components/common/Button";
import Badge from "../../../components/common/Badge";
import { showConfirm } from "../../../utils/popup";
import { useCurrencyFormatter } from "../../../context/RegionalSettingsContext";
import { formatDateTime, formatDate } from "../../../utils/regionalFormat";
import { todayIso } from "../../../utils/date";
import { formatDiscountValue } from "../../../utils/currency";
import { studentOverrideShort } from "../../../utils/carryForwardCopy";
import { enrollmentApiBase } from "../../../utils/enrollmentEditRules";
import { localizeBatchName, studentDisplayName, userDisplayName } from '../../../utils/displayNames';
import { apiErrorMessage } from '../../../utils/apiError';

function rolePath(role) {
    const r = String(role || "").toUpperCase();
    if (r === "MANAGER") return "manager";
    if (r === "COACH") return "coach";
    return "admin";
}

export default function EnrollmentDetail({ role = "ADMIN" }) {
    const { t } = useTranslation();
    const formatMoney = useCurrencyFormatter();
    const { id } = useParams();
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [archiving, setArchiving] = useState(false);
    const navigate = useNavigate();
    const [enrollment, setEnrollment] = useState(null);
    const [batch, setBatch] = useState(null);
    const [batchLoadError, setBatchLoadError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [discount, setDiscount] = useState(null);

    const base = enrollmentApiBase(role);
    const path = rolePath(role);
    const batchesApi = role === "MANAGER"
        ? "/manager/batches"
        : role === "COACH"
            ? "/coach/batches"
            : "/admin/batches";

    const fetchEnrollmentData = async () => {
        setLoading(true);
        setLoadError(false);
        setBatchLoadError(false);
        try {
            const res = await API.get(`${base}/${id}`);
            const e = res.data || {};
            setEnrollment(e);

            if (e.batchId) {
                try {
                    if (role === "COACH") {
                        const listRes = await API.get("/coach/batches", { params: { page: 0, size: 500 } });
                        const content = listRes?.data?.content ?? listRes?.data ?? [];
                        const found = (Array.isArray(content) ? content : []).find(
                            (b) => Number(b.batchId ?? b.id) === Number(e.batchId),
                        );
                        if (found) {
                            setBatch(found);
                        } else {
                            setBatch(null);
                            setBatchLoadError(true);
                        }
                    } else {
                        const batchRes = await API.get(`${batchesApi}/${e.batchId}`);
                        setBatch(batchRes.data);
                    }
                } catch {
                    setBatch(null);
                    setBatchLoadError(true);
                }
            }

            if (e.discountId && role === "ADMIN") {
                try {
                    const discountRes = await API.get(`/admin/discounts/${e.discountId}`);
                    setDiscount(discountRes.data);
                } catch {
                    setDiscount(null);
                }
            } else {
                setDiscount(null);
            }
        } catch (e) {
            console.error(e);
            setEnrollment(null);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEnrollmentData();
    }, [id, role]);

    if (loading) {
        return (
            <PageLoadingShell
                variant="page-shell-form"
                title={t("pages.enrollmentDetail.loadingTitle")}
                subtitle={t("pages.enrollmentDetail.subtitle")}
            />
        );
    }
    if (!enrollment) {
        return (
            <div className="page-shell-form p-6">
                <div className={`rounded-xl border px-4 py-3 text-sm ${loadError ? 'border-red-200 bg-red-50 text-red-800' : 'border-slate-200 bg-slate-50 text-slate-700'}`} role="status">
                    {loadError
                        ? t("pages.enrollmentDetail.loadFailed")
                        : t("pages.enrollmentDetail.notFound")}
                </div>
            </div>
        );
    }

    const effectiveMode = enrollment.overrideChargesMode || (batch && batch.chargesMode);
    const isPeriodBatch = isPeriodBillingMode(effectiveMode);
    const endDate = enrollment.endEnrollmentDate;
    const isExpired = endDate && endDate < todayIso();
    const showRenew = !enrollment.active || isExpired;

    const renewUrl = `/${path}/enrollments/new?renewFrom=${enrollment.enrollmentId}&studentId=${enrollment.studentId}&levelId=${enrollment.levelId}&batchId=${enrollment.batchId}`;

    const formatCarryForward = (value) => {
        if (value === true) return t("pages.enrollmentDetail.enabled");
        if (value === false) return t("pages.enrollmentDetail.disabled");
        return t("pages.enrollmentDetail.followsBatch");
    };

    const discountType = discount
        ? (discount.discountType || discount.type || "PERCENT")
        : (enrollment.appliedDiscountType || "PERCENT");
    const discountValue = discount
        ? Number(discount.discountValue ?? discount.value ?? 0)
        : Number(enrollment.appliedDiscountValue ?? 0);
    const applyDiscount = (amount) => {
        if (!discountValue) return Number(amount || 0);
        if (String(discountType).toUpperCase() === "FLAT") {
            return Math.max(0, Number(amount || 0) - discountValue);
        }
        return Number(amount || 0) * (1 - discountValue / 100);
    };
    const hasDiscount = discountValue > 0;
    const discountDisplay = formatDiscountValue(discountType, discountValue);

    const renderSessionPricing = () => {
        const mode = effectiveMode;
        const isHourly = mode === "PER_SESSION_HOURLY";
        const isFlat = mode === "PER_SESSION";
        if (!isFlat && !isHourly) return null;

        const baseCharge = enrollment.overridePerSessionCharge || (batch && batch.sessionCharge) || 0;
        const finalCharge = applyDiscount(baseCharge);

        return (
            <div className="bg-white rounded-lg p-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    {isHourly ? t("pages.enrollmentDetail.hourlyRate") : t("pages.enrollmentDetail.perSessionCharge")}
                </div>
                {hasDiscount ? (
                    <div>
                        <div className="text-sm text-gray-500 line-through">{formatMoney(baseCharge)}</div>
                        <div className="font-bold text-green-700 text-xl">{formatMoney(finalCharge)}</div>
                        <div className="text-xs text-green-600 mt-1">
                            {t("pages.enrollmentDetail.afterDiscountApplied", {
                                value: discountDisplay,
                                defaultValue: `After ${discountDisplay} discount`,
                            })}
                        </div>
                    </div>
                ) : (
                    <div className="font-semibold text-gray-900 text-lg">{formatMoney(baseCharge)}</div>
                )}
                {enrollment.overridePerSessionCharge && (
                    <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                        {t("pages.enrollmentDetail.enrollmentOverride")}
                    </span>
                )}
            </div>
        );
    };

    const renderPeriodPricing = () => {
        if (!isPeriodBatch) return null;

        const chargeSnapshot = enrollmentChargeSnapshot(enrollment);
        const baseCharge = resolvePeriodCharge(batch, chargeSnapshot);
        const interval = resolvePeriodInterval(batch, chargeSnapshot);
        const finalCharge = applyDiscount(baseCharge);
        const hasOverride = enrollment.overrideChargesMode
            || enrollment.overridePeriodCharge != null
            || enrollment.overridePeriodInterval != null
            || enrollment.overrideMonthlyCharge != null
            || enrollment.overrideYearlyCharge != null;

        return (
            <>
                <div className="bg-white rounded-lg p-4">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t("pages.enrollmentDetail.billingPeriod")}</div>
                    <div className="font-semibold text-gray-900">
                        {formatPeriodInterval(interval) || interval || "—"}
                    </div>
                    {hasOverride && (
                        <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                            {t("pages.enrollmentDetail.enrollmentOverride")}
                        </span>
                    )}
                </div>
                <div className="bg-white rounded-lg p-4">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t("pages.enrollmentDetail.periodFee")}</div>
                    {baseCharge > 0 ? (
                        hasDiscount ? (
                            <div>
                                <div className="text-sm text-gray-500 line-through">{formatMoney(baseCharge)}</div>
                                <div className="font-bold text-green-700 text-xl">{formatMoney(finalCharge)}</div>
                                <div className="text-xs text-green-600 mt-1">
                                    {t("pages.enrollmentDetail.afterDiscountApplied", {
                                        value: discountDisplay,
                                        defaultValue: `After ${discountDisplay} discount`,
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div className="font-semibold text-gray-900 text-lg">{formatMoney(baseCharge)}</div>
                        )
                    ) : (
                        <div className="font-semibold text-gray-500 text-lg">{t("pages.enrollmentDetail.notConfigured")}</div>
                    )}
                </div>
            </>
        );
    };

    return (
        <div className="page-shell-form">
            <PageHeaderSection
                title={t("pages.enrollmentDetail.title", { id: enrollment.enrollmentId })}
                subtitle={t("pages.enrollmentDetail.subtitle")}
                aside={(
                    <>
                        {enrollment.active ? (
                            <Badge variant="success">
                                {isExpired ? t("pages.enrollmentDetail.activeExpired") : t("common.active")}
                            </Badge>
                        ) : (
                            <Badge variant="gray">{t("common.archived")}</Badge>
                        )}
                        {enrollment.active && !isExpired && (
                            <Button variant="warning" onClick={() => setArchiveOpen(true)}>
                                {t("common.archive")}
                            </Button>
                        )}
                        {showRenew && (
                            <Button variant="page-accent" onClick={() => navigate(renewUrl)}>
                                {t("pages.enrollmentDetail.renew")}
                            </Button>
                        )}
                        {!enrollment.active && (
                            <Button
                                variant="success"
                                onClick={async () => {
                                    if (!await showConfirm(t("pages.enrollmentDetail.activateConfirm", { id: enrollment.enrollmentId }))) return;
                                    await activateEnrollment(path, enrollment.enrollmentId);
                                    fetchEnrollmentData();
                                }}
                            >
                                {t("common.activate")}
                            </Button>
                        )}
                    </>
                )}
            />

            {batchLoadError && (
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
                    {t("pages.enrollmentDetail.batchLoadFailed", {
                        defaultValue: "Batch details could not be loaded. Charges and period info may be incomplete.",
                    })}
                </div>
            )}

            <ArchiveEnrollmentModal
                open={archiveOpen}
                enrollmentId={enrollment.enrollmentId}
                studentName={enrollment.studentName}
                enrolledDate={enrollment.enrolledDate}
                defaultEndIso={enrollment.endEnrollmentDate || todayIso()}
                archiving={archiving}
                onCancel={() => setArchiveOpen(false)}
                onConfirm={async (endDate) => {
                    setArchiving(true);
                    try {
                        await archiveEnrollment(path, enrollment.enrollmentId, endDate);
                        setArchiveOpen(false);
                        fetchEnrollmentData();
                    } catch (err) {
                        alert(apiErrorMessage(err, t("pages.enrollmentDetail.archiveFailed")));
                    } finally {
                        setArchiving(false);
                    }
                }}
            />

            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("pages.enrollmentDetail.basicInfo")}</h3>
                <div className="grid md:grid-cols-3 gap-4">
                    <div>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t("enrollments.form.student")}</div>
                        <div className="mt-1 font-medium text-gray-900">
                            <UserLink id={enrollment.studentId} name={enrollment.studentName || studentDisplayName(null, enrollment.studentId, t)} />
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t("enrollments.form.level")}</div>
                        <div className="mt-1 font-medium text-gray-900">{enrollment.levelName || `#${enrollment.levelId}`}</div>
                    </div>
                    <div>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t("enrollments.form.trainingBatch")}</div>
                        <div className="mt-1 font-medium text-gray-900 break-words">{localizeBatchName(enrollment.batchName, t) || (enrollment.batchId != null ? t('batches.batchNumber', { id: enrollment.batchId }) : '—')}</div>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("pages.enrollmentDetail.enrollmentPeriod")}</h3>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t("pages.enrollmentDetail.enrolledDate")}</div>
                        <div className="mt-1 font-medium text-gray-900">{enrollment.enrolledDate ? formatDate(enrollment.enrolledDate) : "-"}</div>
                    </div>
                    <div>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t("enrollments.form.endDate")}</div>
                        <div className="mt-1 font-medium text-gray-900">{enrollment.endEnrollmentDate ? formatDate(enrollment.endEnrollmentDate) : t("pages.enrollmentDetail.openEnded")}</div>
                    </div>
                </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg shadow p-6 border-2 border-purple-200">
                <h3 className="text-lg font-semibold text-purple-900 mb-4">{t("pages.enrollmentDetail.pricingBilling")}</h3>

                <div className="mb-4 p-3 bg-white rounded-lg border-l-4 border-purple-500">
                    <span className="text-sm font-medium text-gray-700">
                        {enrollment.overrideChargesMode ? (
                            <span className="text-amber-700">{t("pages.enrollmentDetail.customCharges")}</span>
                        ) : (
                            <span className="text-blue-700">{t("pages.enrollmentDetail.batchCharges")}</span>
                        )}
                    </span>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t("enrollments.form.chargesMode")}</div>
                        <div className="font-semibold text-gray-900">{formatChargesMode(effectiveMode)}</div>
                    </div>

                    {enrollment.periodChargeStatus && (
                        <div className="bg-white rounded-lg p-4">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t("pages.enrollmentDetail.periodFee")} — {t("common.status")}</div>
                            {(() => {
                                const badge = periodChargeStatusLabel(enrollment.periodChargeStatus);
                                return badge ? (
                                    <>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${badge.className}`}>
                                            {badge.text}
                                        </span>
                                        {enrollment.periodChargeAmount != null && (
                                            <div className="font-semibold text-gray-900 mt-2">{formatMoney(enrollment.periodChargeAmount)}</div>
                                        )}
                                    </>
                                ) : null;
                            })()}
                        </div>
                    )}

                    {renderPeriodPricing()}
                    {renderSessionPricing()}
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                {(enrollment.discountId || enrollment.appliedDiscountValue) && (
                    <div className="bg-green-50 rounded-lg shadow p-6 border-2 border-green-200">
                        <h3 className="text-lg font-semibold text-green-900 mb-4">{t("pages.enrollmentDetail.discountApplied")}</h3>
                        <div className="space-y-2">
                            {discount && (
                                <>
                                    <div>
                                        <div className="text-xs font-medium text-green-700 uppercase tracking-wide">{t("pages.discountForm.nameLabel")}</div>
                                        <div className="mt-1 font-semibold text-green-900">{discount.name}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-medium text-green-700 uppercase tracking-wide">{t("pages.discountForm.valueLabel")}</div>
                                        <div className="mt-1 font-bold text-lg text-green-900">
                                            {formatDiscountValue(discount.discountType, discount.discountValue)}
                                        </div>
                                    </div>
                                </>
                            )}
                            {enrollment.appliedDiscountValue && (
                                <div>
                                    <div className="text-xs font-medium text-green-700 uppercase tracking-wide">{t("pages.discountForm.valueLabel")}</div>
                                    <div className="mt-1 font-bold text-lg text-green-900">
                                        {formatDiscountValue(enrollment.appliedDiscountType, enrollment.appliedDiscountValue)}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {!isPeriodBatch && (
                    <div className="bg-blue-50 rounded-lg shadow p-6 border-2 border-blue-200">
                        <h3 className="text-lg font-semibold text-blue-900 mb-4">{t("pages.enrollmentDetail.carryForward")}</h3>
                        <div>
                            <div className="text-xs font-medium text-blue-700 uppercase tracking-wide">{t("common.status")}</div>
                            <div className="mt-1 font-semibold text-blue-900 text-lg">{formatCarryForward(enrollment.overrideCarryForward)}</div>
                            {enrollment.overrideCarryForward !== null && enrollment.overrideCarryForward !== undefined && (
                                <p className="mt-2 text-sm text-blue-700">
                                    {studentOverrideShort(enrollment.overrideCarryForward)}
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {enrollment.paymentReceiver && (
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('pages.enrollmentDetail.paymentReceiver')}</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('pages.enrollmentDetail.receiverType')}</div>
                            <div className="mt-1 font-medium text-gray-900">
                                {t(`pages.enrollmentDetail.receivers.${enrollment.paymentReceiver}`, {
                                    defaultValue: enrollment.paymentReceiver,
                                })}
                            </div>
                        </div>
                        {enrollment.nameOfReceiver && (
                            <div>
                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('pages.enrollmentDetail.receiverName')}</div>
                                <div className="mt-1 font-medium text-gray-900">{enrollment.nameOfReceiver}</div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('pages.enrollmentDetail.additionalInfo')}</h3>
                <div className="grid md:grid-cols-3 gap-4">
                    <div>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('pages.enrollmentDetail.enrollmentType')}</div>
                        <div className="mt-1 font-medium text-gray-900">
                            {t(`pages.enrollmentDetail.types.${enrollment.enrollmentType || 'REGULAR'}`, {
                                defaultValue: enrollment.enrollmentType || 'REGULAR',
                            })}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('pages.enrollmentDetail.enrolledBy')}</div>
                        <div className="mt-1 font-medium text-gray-900">
                            {enrollment.enrolledBy ? (
                                <UserLink
                                    id={enrollment.enrolledBy}
                                    name={enrollment.enrolledByName || userDisplayName(null, enrollment.enrolledBy, t)}
                                />
                            ) : (
                                "-"
                            )}
                        </div>
                    </div>
                    <div>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('pages.enrollmentDetail.createdAt')}</div>
                        <div className="mt-1 font-medium text-gray-900">
                            {enrollment.created ? formatDateTime(enrollment.created) : "-"}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
